import { useCallback, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '../../AppContext'
import { hasCapability } from '../../auth/roles'
import { PageHeader } from '../../components/primitives/PageHeader'
import { Panel } from '../../components/primitives/Panel'
import { Field } from '../../components/primitives/Field'
import { CheckboxGroup, type CheckboxOption } from '../../components/primitives/CheckboxGroup'
import { Notice } from '../../components/primitives/Notice'
import { Mono } from '../../components/primitives/Fact'
import { TokenRevealModal } from '../../components/TokenRevealModal'
import { PendingOverlay } from '../../components/PendingOverlay'
import { refusalSentence } from '../../theme/vocabulary'
import { SUPPORTED_ENTITLEMENTS, type Entitlement, type PrincipalClass, type TokenIssuanceResponse } from '../../api/types'
import { WizardSteps } from './WizardSteps'
import { EMPTY_INPUT, buildPlan, validate, type PlanStep, type WizardInput, type WizardPhase } from './wizardMachine'
import { recoveryFor, runPlan, type RunResult, type StepResult } from './runPlan'

// "Onboard customer" wizard (ATC-133).
//
// A ROUTE, NOT A MODAL. A 560px modal cannot hold a review table and a receipt,
// and a modal implies dismissability at exactly the moment dismissal is most
// expensive — mid-execution, with a token on screen.
//
// ITS FAILURE SEMANTICS ARE THE FEATURE. Five non-transactional writes across
// three resources, no transaction available and none coming. Nothing is written
// until one clearly-labelled button on a screen that enumerates every write in
// order.
//
// `useBlocker` IS UNAVAILABLE — the app uses component `<Routes>`, not a data
// router — so back-navigation genuinely cannot be blocked. Said plainly rather
// than pretended: the receipt is mirrored to `sessionStorage` (IDENTIFIERS
// ONLY, never token material) so a stray back button loses the screen and not
// the record of what was created.
const RECEIPT_KEY = 'atc.onboarding.receipt'

interface Receipt {
  accountId: string | null
  customerEnvironmentId: string | null
  steps: { id: string; label: string; outcome: string; detail?: string }[]
}

function saveReceipt(result: RunResult) {
  try {
    const receipt: Receipt = {
      accountId: result.accountId,
      customerEnvironmentId: result.customerEnvironmentId,
      // Identifiers and labels only. A token is never written here, and there
      // is deliberately no field that could carry one.
      steps: result.results.map((r) => ({
        id: r.step.id,
        label: r.step.label,
        outcome: r.outcome,
        detail: r.detail,
      })),
    }
    sessionStorage.setItem(RECEIPT_KEY, JSON.stringify(receipt))
  } catch {
    // sessionStorage can throw (private mode, blocked site data). The receipt
    // is a convenience; the on-screen result is the record.
  }
}

export function OnboardCustomer() {
  const { api, role } = useApp()
  const canWrite = hasCapability(role, 'write')

  const [phase, setPhase] = useState<WizardPhase>('collect')
  const [input, setInput] = useState<WizardInput>(EMPTY_INPUT)
  const [progress, setProgress] = useState<StepResult[]>([])
  const [result, setResult] = useState<RunResult | null>(null)

  // The reveal gate. The executor awaits `pending.current`, which resolves only
  // when the operator dismisses the modal.
  const [revealing, setRevealing] = useState<{
    result: TokenIssuanceResponse
    principalClass: PrincipalClass
    readBackWarning?: string
  } | null>(null)
  const pending = useRef<(() => void) | null>(null)

  const reveal = useCallback(
    (issued: TokenIssuanceResponse, principalClass: PrincipalClass, readBackWarning?: string) =>
      new Promise<void>((resolve) => {
        pending.current = resolve
        setRevealing({ result: issued, principalClass, readBackWarning })
      }),
    [],
  )

  function dismissReveal() {
    setRevealing(null)
    const resolve = pending.current
    pending.current = null
    resolve?.()
  }

  const validation = validate(input)
  const plan = validation.ok ? buildPlan(input) : []

  const entitlementOptions: CheckboxOption[] = SUPPORTED_ENTITLEMENTS.map((value) =>
    value === 'ppkg'
      ? {
          value,
          label: value,
          disabled: true as const,
          hint: 'Flight Deck credentials only — tick “Issue a Flight Deck credential” below instead.',
        }
      : { value, label: value },
  )

  const flightdeckOptions: CheckboxOption[] = SUPPORTED_ENTITLEMENTS.map((value) => ({
    value,
    label: value,
    ...(value === 'ppkg' ? { hint: 'The reason this credential class exists.' } : {}),
  }))

  async function execute() {
    setPhase('execute')
    setProgress([])
    const runResult = await runPlan(plan, {
      api,
      input,
      reveal,
      onProgress: setProgress,
    })
    setResult(runResult)
    saveReceipt(runResult)
    setPhase('result')
  }

  if (!canWrite) {
    return (
      <section>
        <PageHeader title="Onboard customer" />
        <Notice tone="warn">Onboarding requires the write capability.</Notice>
      </section>
    )
  }

  return (
    <section className="rx-wizard">
      <PageHeader
        title="Onboard customer"
        back={
          <Link to="/customers" className="rx-page-back">
            Customers
          </Link>
        }
      />
      <WizardSteps current={phase} />

      {phase === 'collect' && (
        <>
          <Panel title="Customer">
            <Field label="Customer name" required error={validation.errors.customerName}>
              <input
                type="text"
                value={input.customerName}
                onChange={(e) => setInput({ ...input, customerName: e.target.value })}
                maxLength={128}
              />
            </Field>
          </Panel>

          <Panel title="Environment">
            {/* ATC-140. The second name field is gone. There is one environment
                per customer, and the label is server-minted — so the wizard
                asks for ONE name, the customer's, instead of two that mostly
                restated each other. */}
            <CheckboxGroup
              legend="Entitled services"
              options={entitlementOptions}
              value={input.entitledServices}
              onChange={(next) => setInput({ ...input, entitledServices: next as Entitlement[] })}
            />
            {validation.errors.entitledServices && (
              <Notice tone="warn">{validation.errors.entitledServices}</Notice>
            )}
            <Field
              label="Expires on"
              hint="Optional. End of the chosen day, UTC — blank means perpetual."
              error={validation.errors.expiryDate}
            >
              <input
                type="date"
                value={input.expiryDate}
                onChange={(e) => setInput({ ...input, expiryDate: e.target.value })}
              />
            </Field>
          </Panel>

          <Panel title="Migration licences">
            <Field
              label="Consumable credits"
              hint="Optional."
              error={validation.errors.consumableCredits}
            >
              <input
                type="number"
                min={1}
                step={1}
                value={input.consumableCredits}
                onChange={(e) => setInput({ ...input, consumableCredits: e.target.value })}
              />
            </Field>
            <Field label="Pool capacity" hint="Optional." error={validation.errors.poolCapacity}>
              <input
                type="number"
                min={1}
                step={1}
                value={input.poolCapacity}
                onChange={(e) => setInput({ ...input, poolCapacity: e.target.value })}
              />
            </Field>
          </Panel>

          <Panel title="Flight Deck credential">
            <label className="rx-check">
              <input
                type="checkbox"
                className="rx-check-input"
                checked={input.issueFlightdeck}
                onChange={(e) => setInput({ ...input, issueFlightdeck: e.target.checked })}
              />
              <span className="rx-check-body">
                <span className="rx-check-label">Issue a Flight Deck credential</span>
                <span className="rx-check-hint">
                  Required for <code>.ppkg</code> builds. Goes on the Manager Function App only —
                  never on a device.
                </span>
              </span>
            </label>
            {input.issueFlightdeck && (
              <CheckboxGroup
                legend="Flight Deck entitlements"
                options={flightdeckOptions}
                value={input.flightdeckEntitlements}
                onChange={(next) =>
                  setInput({ ...input, flightdeckEntitlements: next as Entitlement[] })
                }
              />
            )}
          </Panel>

          <div className="rx-wizard-actions">
            <button
              className="btn-primary"
              disabled={!validation.ok}
              onClick={() => setPhase('review')}
            >
              Review
            </button>
          </div>
        </>
      )}

      {phase === 'review' && (
        <>
          <Panel title="What will be written">
            {/* THE ORDER IS STATED because it DIFFERS from the collection
                order, and the difference is deliberate: the irreversible
                writes go last. */}
            <p className="muted">
              In this order. The reversible writes run first on purpose — a grant against an
              account with no environments is harmless and correctable, whereas issuing an
              environment mints an ID and reveals a token exactly once.
            </p>
            <ol className="rx-wizard-results">
              {plan.map((step: PlanStep, index) => (
                <li className="rx-wizard-result" key={step.id}>
                  <span className="rx-wizard-result-name">
                    <span className="rx-num">{index + 1}.</span> {step.label}
                  </span>
                  <span className="rx-wizard-result-detail">
                    {step.irreversible ? 'not undoable' : 'correctable'}
                  </span>
                </li>
              ))}
            </ol>
          </Panel>

          <Notice tone="warn">
            There is no transaction. If a step fails, everything before it stays — nothing is
            rolled back, because the only “undo” for an issued environment is a revocation that
            never had a business meaning. You will be told exactly what succeeded.
          </Notice>

          <div className="rx-wizard-actions">
            <button onClick={() => setPhase('collect')}>Back</button>
            <button className="btn-primary" onClick={execute}>
              Create {plan.length} record(s)
            </button>
          </div>
        </>
      )}

      {phase === 'execute' && (
        <Panel title="Working">
          {/* Hotfix 053. The per-step ledger below already shows progress, so
              this is only for the window before the FIRST step resolves — on a
              cold start that alone measured 13 seconds. Once anything has
              landed, the ledger is more informative than a loader and the
              overlay gets out of the way. */}
          {progress.length === 0 && !revealing && (
            <PendingOverlay label="Creating the first record…" />
          )}
          <ol className="rx-wizard-results">
            {plan.map((step) => {
              const done = progress.find((r) => r.step.id === step.id)
              return (
                <li className="rx-wizard-result" key={step.id}>
                  <span className="rx-wizard-result-name">{step.label}</span>
                  <span className="rx-wizard-result-detail">
                    {done ? done.outcome : 'waiting'}
                  </span>
                </li>
              )
            })}
          </ol>
        </Panel>
      )}

      {phase === 'result' && result && (
        <>
          {result.complete ? (
            <Notice tone="ok">Customer onboarded.</Notice>
          ) : (
            <Notice tone="error">
              Onboarding stopped part-way. Everything listed as done below was written and is
              still there — finish the rest from the individual pages.
            </Notice>
          )}

          <Panel title="What happened">
            <ol className="rx-wizard-results">
              {result.results.map((r) => (
                <li className="rx-wizard-result" key={r.step.id}>
                  <span className="rx-wizard-result-name">
                    {r.step.label}
                    {r.outcome === 'failed' && r.failure && (
                      <div className="rx-check-hint">
                        {refusalSentence(r.failure.code, r.failure.message)}
                      </div>
                    )}
                    {/* ATC-139. A step that SUCCEEDED but did less than it
                        said. It survives the reveal modal's dismissal here,
                        because the token dialog is transient and this record
                        is what the operator scrolls back to. */}
                    {r.readBackWarning && (
                      <div className="rx-check-hint">{r.readBackWarning}</div>
                    )}
                  </span>
                  <span className="rx-wizard-result-detail">
                    {r.outcome === 'ok' ? (r.detail ?? 'done') : r.outcome}
                  </span>
                </li>
              ))}
            </ol>
          </Panel>

          {result.results
            .filter((r) => r.readBackWarning)
            .map((r) => (
              <Notice key={`readback-${r.step.id}`} tone="error">
                {r.readBackWarning}
              </Notice>
            ))}

          {!result.complete &&
            result.results
              .filter((r) => r.outcome === 'failed')
              .map((r) => {
                const recovery = recoveryFor(r, result.accountId)
                return (
                  <Notice key={r.step.id} tone="warn">
                    {recovery.message}
                    {recovery.href && (
                      <>
                        {' '}
                        <Link to={recovery.href}>{recovery.linkLabel ?? 'Open'}</Link>
                      </>
                    )}
                  </Notice>
                )
              })}

          {/* NO "START OVER" BUTTON, EVER. Re-running a plan whose first steps
              already succeeded creates a duplicate customer and a second
              environment; on an ambiguous failure it may duplicate the very
              record that did land. The links below go to the standalone levers,
              which is where a half-finished onboarding is finished. */}
          <Panel title="Where to go next">
            <ul className="checklist">
              {/* ATC-141. NAMES, not GUIDs. This screen closes a five-step
                  provisioning flow, and it was printing two identifiers at an
                  operator who had just typed the customer's name and been
                  shown the environment's — neither of which appeared. The id
                  stays as the secondary line, because it is still the thing
                  pasted into a ticket. */}
              {result.accountId && (
                <li>
                  <Link to={`/customers/${result.accountId}`}>
                    Customer {input.customerName.trim() || result.accountId}
                  </Link>
                  <div>
                    <Mono>{result.accountId}</Mono>
                  </div>
                </li>
              )}
              {result.customerEnvironmentId && (
                <li>
                  <Link to={`/environments/${result.customerEnvironmentId}`}>
                    Environment {result.environmentLabel ?? result.customerEnvironmentId}
                  </Link>
                  <div>
                    <Mono>{result.customerEnvironmentId}</Mono>
                  </div>
                </li>
              )}
              <li>
                <Link to="/customers">All customers</Link>
              </li>
            </ul>
          </Panel>
        </>
      )}

      {revealing && (
        <TokenRevealModal
          result={revealing.result}
          principalClass={revealing.principalClass}
          variant="issued"
          readBackWarning={revealing.readBackWarning}
          // ATC-141: the wizard KNOWS the customer — it just created the
          // account — so the reveal can name it. A bare issuance cannot, and
          // omits the row rather than inferring one.
          customerName={input.customerName.trim()}
          onClose={dismissReveal}
        />
      )}
    </section>
  )
}
